import { useCallback, useContext, useState, createContext, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import DocumentContext from './DocumentContext.context';
import { ControlledField } from '@shared/components';
import { yupUrlTransform, isInsecureUrl } from '@shared/utils/url';

type TMenuContext = {
  open: (href?: string) => any;
};
// @ts-ignore
const MenuContext = createContext<TMenuContext>(null);
export default MenuContext;

const schema = yup.object({
  text: yup.string().required(),
  href: yup
    .string()
    .required()
    .transform(yupUrlTransform)
    .test('https-only', 'Only HTTPS links are allowed – HTTP is not secure', (v) => !v || !isInsecureUrl(v))
    .url('Must be a valid URL'),
});
type TFormData = yup.InferType<typeof schema>;

type TProps = {
  children: ReactNode;
};
export const MenuProvider = ({ children }: TProps) => {
  const { editor } = useContext(DocumentContext);

  const [isOpen, setIsOpen] = useState(false);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { text: '', href: '' },
  });

  const onSubmit = useCallback((data: TFormData) => {
    editor.chain().focus().extendMarkRange('link').insertContent(`<a href="${data.href}">${data.text}</a>`).run();

    /*
    editor.chain().focus().extendMarkRange('link').setLink({ href: data.href }).command(({ tr }) => {
      tr.insertText(data.text);
      return true;
    }).run();
    */

    /*
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" })
      .run();
    */

    setIsOpen(false);
  }, []);

  const open = (href?: string) => {
    const { view, state } = editor;
    const { from, to } = view.state.selection;
    const text = state.doc.textBetween(from, to, '');

    setValue('text', text);
    setValue('href', href ? href : '');
    setIsOpen(true);
  };

  // Unfortunately, we have to use a controller here:
  return (
    <MenuContext.Provider value={{ open }}>
      <Dialog
        open={isOpen}
        onOpenChange={(_, data) => {
          setIsOpen(data.open);
          if (!data.open) editor.commands.focus();
        }}
      >
        <DialogSurface aria-describedby={undefined}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogBody>
              <DialogTitle>URL</DialogTitle>
              <DialogContent className="flex flex-col gap-2">
                <ControlledField label="Display text" name="text" control={control} errors={errors} required />
                <ControlledField label="URL" name="href" control={control} errors={errors} required />
              </DialogContent>
              <DialogActions>
                <Button type="submit" appearance="primary">
                  Ok
                </Button>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>

      {children}
    </MenuContext.Provider>
  );
};
